/// <reference types="chrome"/>
import { Injectable, OnDestroy, OnInit } from "@angular/core"
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { from, Observable, of, Subject, Subscription } from 'rxjs';
import { concatMap, delay as timedelay, takeWhile } from 'rxjs/operators';
import { EXTENSION_ID } from '../../common/constants/utils.constants';
import { ScriptProcessorService } from '@/services/script-processor.service';
import { UserAppService } from '@/services/user-apps.service';

import * as Sentry from '@sentry/capacitor'
import { DeviceNetworkService } from "./device-network.service"
import { Capacitor } from '@capacitor/core';

declare let window: any;

const ERROR_MESSAGE = 'Something went wrong. Please try again later.';

@Injectable({
  providedIn: 'root'
})
export class BrowserService implements OnDestroy, OnInit {

  private subject = new Subject<any>()
  private showLoader = new Subject<any>()
  private dialog = new Subject<any>()

  __show_logs__: boolean = true
  iabConfig: string = 'toolbar=yes,toolbarposition=top,toolbartranslucent=no,toolbarcolor=#ffffff,hidenavigationbuttons=no,navigationbuttoncolor=#FFA500,hideurlbar=yes,fullscreen=yes,hidden=yes,clearsessioncache=yes,clearcache=yes'
  iab: any
  iabDefault: any
  user_app_auth: any
  studentAppAuthSubscription: Subscription

  authData: string[]

  js_repeat_subscription: Subscription

  // Utils
  exid: string = EXTENSION_ID

  loadStopEventTriggered = false
  isBrowserPlatform: boolean

  script_login: { action: string, payload: any }[]
  userAppSubscription: Subscription

  constructor(
    private iabSvc: InAppBrowser,
    private userAppService: UserAppService,
    private scriptProcessorService: ScriptProcessorService,

    public platform: Platform,
    private network: DeviceNetworkService,
  ) {

    this.isBrowserPlatform = false

    if (platform.is('android')) {
      this.iabConfig += ',location=yes'
    } else if (platform.is('ios')) {
      this.iabConfig += ',location=no,cleardata=yes'
    } else {
      // browser
    }

    this.getMessage().subscribe((data) => {
      if (data === 'start') {
        this.process_first_item()

      } else if (data === 'next') {
        // Pop current item from queue (as current item is done)
        let data = JSON.parse(sessionStorage.getItem('ch_script_items'))
        data.shift()
        sessionStorage.setItem('ch_script_items', JSON.stringify(data))

        if (data.length > 0)
          this.process_first_item()

      } else {
        this.log('Nothing to process')
      }
    })


    this.scriptProcessorService
      .getSiteLoading()
      .subscribe(showLoader_two => {
        if (showLoader_two) {
          this.showLoader.next({ showLoader_two });
        } else {
          this.showLoader.next(void 0);
        }
      })

  }

  async ngOnInit() {
    await this.network.initializeNetworkEvents()
  }

  asciiToHex(str: string){
    const array = []
    for (let index = 0, l = str.length;  index < l; index++) {
        const hex = Number(str.charCodeAt(index)).toString(16)
        array.push(hex)
    }

    return array.join('')
  }

  log = (...msg) => { if (this.__show_logs__) console.log("👉 ", ...msg) }

  // Public Methods
  // --------------------------

  sendMessage(message: string) {
    this.subject.next(message)
  }

  getMessage(): Observable<any> {
    return this.subject.asObservable()
  }

  showSiteLoading(showLoader: string) {
    this.showLoader.next({ showLoader });
  }

  clearSiteLoading() {
    this.showLoader.next(void 0);
  }

  delayedClearSiteLoading() {
    setTimeout(() => {
      this.clearSiteLoading();
    }, 2000)
  }

  getSiteLoading(): Observable<any> {
    return this.showLoader.asObservable();
  }


  // Dialog
  showDialog(dialog: any) {
    this.dialog.next({ dialog });
  }

  showErrorDialog(msg) {
    let data = { "msg": msg, "icon": "https://files.getclasshive.com/files/icon-error/icon-error.png", "terminate": true }
    this.showDialog(data);
  }

  clearDialog() {
    this.dialog.next(void 0);
  }

  getDialog(): Observable<any> {
    return this.dialog.asObservable();
  }


  openIABrowser(
    link: string,
    desktop_mode: number = 0,
    app_user_agent: string = ''
  ) {
    if (desktop_mode) {
      this.iabConfig += `,desktopmode=yes`
    }

    if (app_user_agent) {
      const encodedUserAgent = this.asciiToHex(app_user_agent)
      this.iabConfig += `,useragent=${encodedUserAgent}`
    }
    this.iabDefault = this.iabSvc.create(link, '_blank', this.iabConfig);
    this.iabDefault.show();
  }

  openBrowser(link: string[], actions: any[] = []) {
    const url = {
      action: 'go-to-url',
      payload: link
    };

    chrome.runtime.sendMessage(
      this.exid,
      {
        messageFromWeb: [
          url,
          ...actions
        ]
      },
      (r) => { }
    );
  }


  openLink(login_link: string[], desktop_mode=0, app_user_agent='') {

    this.isBrowserPlatform = Capacitor.getPlatform() === 'web';

    this.loadStopEventTriggered = false;
    this.showSiteLoading('show');

    try {
      if (this.isBrowserPlatform) {
        this.openBrowser(login_link)
      } else {
        this.openIABrowser(login_link[0], desktop_mode, app_user_agent)
      }
      this.delayedClearSiteLoading()
    } catch(e) {
      this.showErrorDialog(ERROR_MESSAGE)
      this.clearSiteLoading()
      throw e
    }
  }

  appLogin(appId: number, userId: string) {
    this.log(`Loading the app with ID ${appId}`);

    Sentry.setContext('appLogin', {
      appId,
      userId,
      platforms: this.platform.platforms(),

    })

    this.isBrowserPlatform = Capacitor.getPlatform() === 'web';
    let params = {};

    if (this.isBrowserPlatform) {
      params = {
        browser: 1
      };
    }

    this.loadStopEventTriggered = false;
    this.showSiteLoading('show');

    this.userAppSubscription = this.userAppService
      .getUserAppScriptLogin(userId, appId, params)
      .subscribe(
        data => {
          const {status, result} = data;

          if (status && status !== 200 || !this.is_valid_script_login(result.script_login)) {
            this.log(`Error ${data.message}`)
            this.showErrorDialog(ERROR_MESSAGE);
            this.clearSiteLoading();
            return;
          }

          const {
            script_login,
            login_link,
            is_auth_data_complete,
            user_auth_data,
            app_user_agent,
            app_inject_script,
            desktop_mode,
            meta
          } = result

          this.authData = user_auth_data

          if (!this.is_valid_script_login(script_login)) {
            this.showErrorDialog(ERROR_MESSAGE)
            this.clearSiteLoading();
            return;
          }

          if (this.isBrowserPlatform) {
            this.openBrowser(login_link, script_login);
            this.delayedClearSiteLoading()
            return
          }

          if (desktop_mode) {
            this.iabConfig += `,desktopmode=yes`
          }

          if (app_user_agent) {
            const encodedUserAgent = this.asciiToHex(app_user_agent)
            this.iabConfig += `,useragent=${encodedUserAgent}`
          }

          if (app_inject_script) {
            const encodedInjectScript = this.asciiToHex(app_inject_script)
            this.iabConfig += `,injectscript=${encodedInjectScript}`
          }

          Sentry.setContext('iab', {
            ua: app_user_agent,
            desktop_mode,
            user_auth_data
          })

          this.iab = this.iabSvc.create(login_link, '_blank', this.iabConfig)

          // The `loaderror` callback also triggers when vendor app have a request that was terminated
          // This is common eg. when user clicks back button during api request.
          // Hence, `loaderror` should not be use. Retaining for reference
          // this.iab.on('loaderror').subscribe(() => {})

          let init = false;
          this.iab.on('loadstart').subscribe(() => {
            this.log(`loadstart event triggered`)

            if (!init) {
              this.iab.executeScript({
                code: `(function() {
                localStorage.clear();
                return true;
              })()`
              }, null)

              init = true
            }
          })

          this.iab.on('loadstop').subscribe(() => {
            this.log(`Loadstop event triggered`)

            try {

              // Fixes bug when suppliers app uses `window.open`
              this.iab.executeScript({'code': 'window.open = function(url){ window.location.href=url; };'}, null)

              // loadstop event will trigger again when "go_to_url" (or "go-to-url") is executed,
              // which in turn re-process again from the start causing unterminated loop
              // This fixes unterminated loop
              if (this.loadStopEventTriggered) return
              this.loadStopEventTriggered = true

              if (script_login && script_login.length > 0) {
                if (is_auth_data_complete) {

                  if (meta.script_processor_version == 1) {
                    this.log('Script Processor v1')
                    this.process_scripts(script_login)
                  } else {
                    this.log('Script Processor v2')
                    this.scriptProcessorService.iab = this.iab
                    this.scriptProcessorService.start(script_login)
                  }

                }

              } else {
                // Show sidebar
                if (
                  this.authData
                  && Object.keys(this.authData).length > 0
                ) {
                  this.log('Sidebar is enabled')
                  this.iab.executeScript({code: this.sidebarScript()}, null)
                }
              }
              this.iab.show();
              this.delayedClearSiteLoading();
            } catch (e) {
              console.log(e);
              this.iab.close();
              this.showErrorDialog(ERROR_MESSAGE);
              this.clearSiteLoading();
            }


          }); // .subscribe

          this.iab.on('exit').subscribe(() => {
            if (this.userAppSubscription) {
              this.userAppSubscription.unsubscribe();
            }
            if (this.js_repeat_subscription) {
              this.js_repeat_subscription.unsubscribe();
            }
            this.iab = null
          }, this.log)

        },
        error => {
          this.log(error);

          let message = ERROR_MESSAGE;
          if (this.network.type === 'none') {
            message = 'No internet connection detected';
          }

          this.showErrorDialog(message);
          this.clearSiteLoading();
          throw new Error(error);
        }
      )

  }


  // ------------------------------------------------------------------------------
  // PRIVATE METHODS
  // ------------------------------------------------------------------------------

  private process_scripts(scripts) {
    sessionStorage.setItem('ch_script_items', JSON.stringify(scripts))
    this.sendMessage('start')
  }

  private process_first_item() {
    let data = JSON.parse(sessionStorage.getItem('ch_script_items'))
    this.log(`Total in-queue', ${data.length}`)

    if (data.length < 1) return

    // Process the 1st item
    let item = data[0]

    switch (item.action) {
      case 'go_to_url':
      case 'go-to-url':
        this.log(`Processing ${item.action}`)
        this.go_to_url(item.payload);
        break;

      case 'js':
        this.log(`Processing ${item.action}`)
        this.js(item.payload);
        break;

      case 'raw-javascript':
      case 'raw_javascript':
        this.log(`Processing ${item.action}`)
        this.raw_javascript(item.payload);
        break;

      case 'js_repeat':
        this.log(`Processing ${item.action}`)
        this.js_repeat(item.payload);
        break;

      case 'show_msg':
        this.log(`Processing ${item.action}`)
        this.show_msg(item.payload, false);
        break;

      case 'show_msg_and_exit':
        this.log(`Processing ${item.action}`)
        this.show_msg_and_exit(item.payload);
        break;

      case 'sleep':
        this.log(`Processing ${item.action}`)
        this.sleep(item.payload);
        break;

      case 'default':
        this.log(`Param "action" not found ${item.action}`)
    }

  }

  private go_to_url(payload) {
    if (!payload) {
      this.show_alert('payload is required')
      return
    }

    let code = `window.location.href = '${payload}';`
    let wrapped_code = this.IIFE_wrapper(code)

    this.sendMessage('next')
  }

  private raw_javascript(payload) {
    if (!payload) {
      this.show_alert('payload is required')
      return
    }

    // Callback was removed - to make it same behavior as raw-javascript
    this.iab.executeScript({ "code": payload }, null)

    this.sendMessage('next')
  }

  private js(payload) {
    if (!payload) {
      this.show_alert('payload is required')
      return
    }

    let wrapped_payload = this.IIFE_wrapper(payload)
    this.iab.executeScript({ "code": wrapped_payload }, (res) => {
      if (res[0] === true) {
        this.sendMessage('next')
      }

    })
  }

  private js_repeat(payload) {
    let { code, delay, retry } = payload

    if (!code) {
      this.show_alert('param "code" missing')
      return
    }

    if (!delay) delay = 0
    if (!retry) retry = 1

    this.exec_js_repeat(code, delay, retry)
  }

  private sleep(payload) {
    if (!payload) {
      this.show_alert('payload is required')
      return
    }

    let observable$ = of('_').pipe(timedelay(payload))

    observable$.subscribe((res) => {
      this.sendMessage('next')
    })
  }

  private show_msg(payload, terminate) {
    let { condition, msg, icon } = payload

    if (!icon) icon = 'https://files.getclasshive.com/files/icon-info/info.png'

    if (!condition) {
      this.show_alert('param "condition" missing')
      return
    }
    if (!msg) {
      this.show_alert('param "msg" missing')
      return
    }

    this.iab.executeScript({ "code": this.IIFE_wrapper(condition) }, (res) => {
      if (res[0] === true) {
        this.iab.hide()
        this.showDialog({ "msg": msg, "icon": icon, "terminate": terminate })
      }

      this.sendMessage('next')
    })
  }

  private show_msg_and_exit(payload) {
    this.show_msg(payload, true)
  }

  private exec_js_repeat(code, time_delay, max_retries) {
    let retry_slots = Array.from({ length: max_retries }, (_, i) => i + 1) // eg. max_retries = 5. output: [1,2,3,4,5]
    sessionStorage.setItem('vendor_returned_value', 'null')

    code = this.IIFE_wrapper(code)

    let callback_rv = false
    this.js_repeat_subscription = from(retry_slots)
      .pipe(
        concatMap(item => of(item).pipe(timedelay(time_delay))),
        takeWhile(item => item <= max_retries && !callback_rv)
      )
      .subscribe(item => {
        this.iab.executeScript({ "code": code }, (res) => {

          if (res[0] === true) { // vendor callback should return boolean

            this.js_repeat_subscription.unsubscribe()
            this.sendMessage('next')
          }

          if (item >= max_retries) {
            // Reached max retries. Giving up..
            this.js_repeat_subscription.unsubscribe();
          }

        })
      })
  }


  private sidebarScript() {
    const authData = this.authData;
    let htmlData = ''
    const capitalizeText = (text) => text.charAt(0).toUpperCase() + text.slice(1)
    const mapKey = (key) => {
      return key.split('_').map(text => capitalizeText(text)).join(' ')
    }

    for (var key in authData) {
      const transformKey = mapKey(key)
      htmlData += "<p class='sidebar-content-label'>" + transformKey + "&nbsp;<span class='sidebar-content-data'>" + authData[key] + "</span></p>"
    }

    return `\

      function classhive_main() {

        if (sessionStorage.getItem('has_shown_sidebar')=='true') {
          console.log('stopped further script execution');
          return;
        }

        if (sessionStorage.getItem('is_sidebar_closed')=='true') {
          console.log('sidebar already closed by user');
          return;
        }

        // Create JS functions
        // -----------------------
        var funcs = document.createElement('script');
        funcs.type = 'text/javascript'
        funcs.innerHTML = \`
          // Hide sidebar
          function classhive_hide_sidebar() {
            var sbar = document.getElementById('classhive_sidebar');
            sbar.remove();
            // sbar.style.display='none';
            document.body.classList.remove('classhive_body_class');
            sessionStorage.setItem('is_sidebar_closed', 'true');
          }

          // Close IAB
          function classhive_close_iab() {
            console.log('closing iab..')
            window.close();
          }
        \`;

        // Dynamically Insert Fields
        // -----------------------
        var insert_label_func = document.createElement('script');
        insert_label_func.type = 'text/javascript'
        insert_label_func.innerHTML = \`

          (function () {
            var contentBox = document.getElementById('contentBox');
            contentBox.innerHTML = "${htmlData}"
          })();

        \`;


        // Create CSS class
        // -----------------------
        var classhive_css = document.createElement('style')
        classhive_css.innerHTML = \`
          .classhive_body_class {
            position:relative;
            left:200px;
            margin-right:200px;
          }

          // Reset this div to not clash with the suppliers app
          #classhive_sidebar,
          #classhive_sidebar::before,
          #classhive_sidebar::after,
          #classhive_sidebar *
          #classhive_sidebar *::before,
          #classhive_sidebar *::after {
            all: revert;
          }

          #classhive_sidebar {
            font-family: 'Montserrat', sans-serif;
            z-index: 9999999;
            position: fixed;
            display: flex;
            flex-direction: column;
            align-items: center;
            top: 0;
            left: 0;
            width: 200px;
            height: 100%;
            background-color: #FFA700;
            color: #fff;
            width: 281px;
            border-right: 4px solid #FFFFFF;
          }

          .sidebar-logo {
            margin: 32px auto;
            width: 94px;
            height: 82px;
          }

          .hidden {
            display: none;
          }

          .content-box {
            color: #797878;
            position: relative;
            z-index: 3;
            top: 0;
            width: 90%;
            display: flex;
            align-items: center;
            flex-direction: column;
            text-align: left;
            justify-content: center;
            background-color: #FFFFFF;
            padding: 20px;
            border-radius: 13px;
          }

          .content-box p {
            margin: 5px 0 !important;
          }

          .content-box p.sidebar-content-label {
            font-size: 16px;
            font-weight: 600;
            color: #FFA700;
            width: 100%;
          }

          .content-box p span.sidebar-content-data {
            font-size: 22px;
            font-weight: 500;
            color: #797878;
          }

          .content-box .content-divider {
            border: 1px solid #FFA700;
            width: 100%;
            margin: 10px auto 5px;
          }

          #classhive_sidebar button {
            color: #FFF !important;
            background-color: #DA4167;
            width: 60%;
            height: 47px;
            border-radius: 5px;
            margin: 32px auto;
            border: none;
            font-weight: 600;
            font-size: 2rem;
          }
        \`;


        // Create sidebar
        // -----------------------
        var classhive_sidebar = document.createElement('div');
        classhive_sidebar.id = 'classhive_sidebar';
        classhive_sidebar.innerHTML = \`
          <img
            class="sidebar-logo"
            src="https://s3.ap-southeast-2.amazonaws.com/files.classhive.com.au/img/classhive_logo_1.png"
          >
          <div id="contentBox" class="content-box"></div>
          <button onclick='classhive_hide_sidebar()'>Done!</button>
        \`

        document.body.appendChild(funcs);
        document.body.appendChild(classhive_css);
        document.body.classList.add('classhive_body_class');
        document.body.appendChild(classhive_sidebar);
        document.body.appendChild(insert_label_func);

        sessionStorage.setItem('has_shown_sidebar', 'true');
      };

      classhive_main();

    `;

  }



  //===================================================================
  //  _   _ _   _ _ _ _   _
  // | | | | |_(_) (_) |_(_) ___  ___
  // | | | | __| | | | __| |/ _ \/ __|
  // | |_| | |_| | | | |_| |  __/\__ \
  //  \___/ \__|_|_|_|\__|_|\___||___/
  //

  private show_alert(msg) {
    // Show native device dialog box
    alert(msg)
  }

  private replaceAll(str, find, replace) {
    return str.replace(new RegExp(find, 'g'), replace);
  }

  private IIFE_wrapper(code) {
    return `\
            (function(){
              try {

                ${code}

                return true;
              }
              catch(err) {
                return false
              }
            })();`;

  }

  private isValidUrl(str) {
    const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[\\/-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(str);
  }

  private is_valid_script_login(data) {
    if (!!data)
      if (Array.isArray(data))
        return true

    return false
  }

  ngOnDestroy() {
    // This is not being triggered. Use this.iab.on('exit') instead
  }

}
